import type {
  CorrectionInput,
  Evidence,
  FrameworkSkill,
  ThreeAxisRecommendation,
} from "@shared/labs/three-axis";
import { useCallback } from "react";
import { rpcClient } from "@/lib/rpc";

export interface CorrectionResponse {
  correctionId: string;
  updatedEvidence?: Evidence;
  updatedAxis?: ThreeAxisRecommendation["axes"][number];
  provisionalAxisValue?: number;
  /** Skill correction roll-up — server replays buildThreeAxisRecommendation
   *  so the client can refresh the edited skill, the skills axis, and the
   *  applied level (which can shift when a skill change clears or breaks a
   *  hire-floor). Without this, the row showed the new "L4" but the rest of
   *  the report kept the old applied level, total dots, and skills axis. */
  updatedFrameworkSkill?: FrameworkSkill;
  updatedSkillsAxis?: ThreeAxisRecommendation["axes"][number];
  updatedAppliedLevel?: ThreeAxisRecommendation["appliedLevel"];
  updatedFloors?: ThreeAxisRecommendation["floors"];
}

export interface UseCorrectionHandlerArgs {
  assessmentId: string;
  threeAxis: ThreeAxisRecommendation | undefined;
  setThreeAxis: (next: ThreeAxisRecommendation) => void;
  onError?: (message: string) => void;
}

/** Resolve which kind of correction this is when the caller did not hint via
 *  `kind`. Order matters: evidence ids live in a different namespace from
 *  framework skill ids, and axis ids are always "gca" | "compWay" | "skills". */
function resolveKind(
  rec: ThreeAxisRecommendation,
  input: CorrectionInput,
): "evidence" | "axis" | "skill" {
  if (input.kind) return input.kind;
  if (rec.evidences.some((e) => e.id === input.evidenceId)) return "evidence";
  if (rec.axes.some((a) => a.id === input.evidenceId)) return "axis";
  return "skill";
}

export function useCorrectionHandler({
  assessmentId,
  threeAxis,
  setThreeAxis,
  onError,
}: UseCorrectionHandlerArgs) {
  return useCallback(
    async (input: CorrectionInput): Promise<{ correctionId: string } | undefined> => {
      if (!threeAxis) return;
      // Refuse to submit when archivedEvaluationId hasn't landed yet.
      // The streaming flow sends `done` before `archived`, so the report
      // is on screen briefly before the archive id is known. The
      // pre-fix fallback to linkedinUrl wrote orphan rows that read as
      // "(perfil removido)" in the audit until manually relinked.
      if (!assessmentId) return;
      const kind = resolveKind(threeAxis, input);

      let response: CorrectionResponse;
      try {
        response = (await rpcClient.labs.candidateAssessmentSubmitCorrection({
          id: assessmentId,
          ...input,
          kind,
          assessmentSnapshot: threeAxis,
        })) as CorrectionResponse;
      } catch (e: any) {
        onError?.(`Falha ao salvar correção: ${e?.message ?? "erro desconhecido"}`);
        return;
      }

      if (kind === "evidence" && response.updatedEvidence && response.updatedAxis) {
        setThreeAxis({
          ...threeAxis,
          evidences: threeAxis.evidences.map((e) =>
            e.id === input.evidenceId ? response.updatedEvidence! : e,
          ),
          axes: threeAxis.axes.map((a) =>
            a.id === response.updatedAxis!.id ? response.updatedAxis! : a,
          ),
        });
      } else if (kind === "axis") {
        // Axis corrections park the patch for review — reflect the provisional
        // value on the card so the CHRO sees their input while the job runs.
        const clamped = Math.max(0, Math.min(1, input.newScore));
        setThreeAxis({
          ...threeAxis,
          axes: threeAxis.axes.map((a) => {
            if (a.id !== input.evidenceId) return a;
            const originalScore = a.chroCorrection?.originalScore ?? a.value;
            return {
              ...a,
              value: clamped,
              pass: clamped >= a.threshold,
              margin: clamped - a.threshold,
              closeCall: Math.abs(clamped - a.threshold) <= 0.05,
              chroCorrection: {
                originalScore,
                correctedScore: clamped,
                rationale: input.rationale,
                correctedAt: new Date().toISOString(),
                correctedBy: "chro",
              },
            };
          }),
        });
      } else if (kind === "skill") {
        // Prefer the server's roll-up when available — it replays
        // buildThreeAxisRecommendation so the skills axis + appliedLevel
        // reflect the new dot count. Fall back to a local patch on the
        // single skill row if the server response is missing the recompute
        // (older deploys, mocked fixtures, etc.).
        const lvl = Math.max(1, Math.min(5, Math.round(input.newScore))) as 1 | 2 | 3 | 4 | 5;
        const assignedKey = `L${lvl}` as const;
        const fallbackDots = Math.max(0, Math.min(5, Math.round(input.newScore)));
        const fallbackSkill = threeAxis.frameworkSkills.find((s) => s.id === input.evidenceId);
        const patchedSkill: FrameworkSkill | undefined =
          response.updatedFrameworkSkill ??
          (fallbackSkill
            ? { ...fallbackSkill, assigned: assignedKey, assignedDots: fallbackDots }
            : undefined);
        const nextFrameworkSkills = patchedSkill
          ? threeAxis.frameworkSkills.map((s) => (s.id === input.evidenceId ? patchedSkill : s))
          : threeAxis.frameworkSkills;
        const nextAxes = response.updatedSkillsAxis
          ? threeAxis.axes.map((a) => (a.id === "skills" ? response.updatedSkillsAxis! : a))
          : threeAxis.axes;
        setThreeAxis({
          ...threeAxis,
          frameworkSkills: nextFrameworkSkills,
          axes: nextAxes,
          ...(response.updatedAppliedLevel ? { appliedLevel: response.updatedAppliedLevel } : {}),
          ...(response.updatedFloors ? { floors: response.updatedFloors } : {}),
        });
      }

      return { correctionId: response.correctionId };
    },
    [assessmentId, threeAxis, setThreeAxis, onError],
  );
}
